import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../Common/Configurations/AuthContext';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import Header from '../../Common/Layout/Header';
import MasterTabs from '../AllTabs/MasterTabs';
import { format, differenceInDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const FeatureItem = ({ icon, text }) => ( <li className="d-flex align-items-center mb-3"><i className={`${icon} fs-4 me-3 text-primary`}></i><span style={{ fontWeight: 500 }}>{text}</span></li> );
const DisabledFeatureItem = ({ icon, text }) => ( <li className="d-flex align-items-center mb-3"><i className={`${icon} fs-4 me-3 text-danger`}></i><span style={{ fontWeight: 500, color: '#dc3545' }}>{text}</span></li> );
const planEnumMap = { 1: 'Monthly Starter', 2: 'Monthly Pro', 3: 'Monthly Enterprise', 4: 'Yearly Starter', 5: 'Yearly Pro', 6: 'Yearly Enterprise', 7: 'Yearly Custom' };
const statusEnumMap = { 0: 'Inactive', 1: 'Active', 2: 'Past Due', 3: 'Canceled', 4: 'Trialing', 5: 'Pending' };
const billingCycleEnumMap = { 1: 'Month', 2: 'Year' };
const websiteCapabilityMap = { 0: 'None', 1: 'Packages Only', 2: 'Flights, Hotels & Packages' };

// KEY CHANGE 1: A brand new, more informative TrialUpsellView component
const TrialUpsellView = () => {
    const navigate = useNavigate();
    const { user } = useAuth(); // Get user info from context

    const trialFeatures = [
        { icon: 'ri-copper-coin-line', text: `${user?.connects || 0} Connects Included` },
        { icon: 'ri-search-line', text: 'Search Flights & Hotels' },
        { icon: 'ri-magic-line', text: 'AI-Powered Itinerary Generation' },
    ];

    const premiumFeatures = [
        { icon: 'ri-whatsapp-line', text: 'AI WhatsApp Chatbot' },
        { icon: 'ri-dashboard-line', text: 'Social Media Lead Capture' },
        { icon: 'ri-window-line', text: 'Launch Your Own Travel Website' },
        { icon: 'ri-key-2-line', text: 'Use Your Own Supplier API Keys' },
        { icon: 'ri-server-line', text: 'Own Website Server & Domain' },
    ];
    
    const daysLeft = user?.endDate ? differenceInDays(new Date(user.endDate), new Date()) : 0;

    return (
        <div className="row">
            {/* Column 1: Trial Status */}
            <div className="col-lg-4">
                <h4 className="fw-bold mb-4">Trial Status</h4>
                <div className="p-4 rounded h-100" style={{ border: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0 fw-semibold">TRIAL PLAN</h5>
                        <span className="badge fs-6 bg-info">Active</span>
                    </div>
                    <h2 className="fw-bolder">{daysLeft > 0 ? daysLeft : 0} Days Left</h2>
                    <p className="text-muted">Your trial ends on {user?.endDate ? format(new Date(user.endDate), 'dd MMM, yyyy') : 'N/A'}.</p>
                    <hr/>
                    <div className="text-center">
                        <button className="btn btn-success" onClick={() => navigate('/subscriptionplans')}>
                            View Upgrade Options
                        </button>
                    </div>
                </div>
            </div>

            {/* Column 2: What's Included */}
            <div className="col-lg-4">
                <h4 className="fw-bold mb-4">Features Included in Your Trial</h4>
                <div className="p-4">
                    <ul className="list-unstyled">
                        {trialFeatures.map((feature, index) => <FeatureItem key={index} {...feature} />)}
                    </ul>
                </div>
            </div>

            {/* Column 3: Upgrade Prompt */}
            <div className="col-lg-4">
                <h4 className="fw-bold mb-4">Upgrade to Unlock</h4>
                <div className="p-4 rounded h-100" style={{ backgroundColor: '#FFF9F9', border: '1px solid #FFEBEB' }}>
                    <p className="fw-semibold text-muted">Upgrade your plan to activate these powerful features!</p>
                    <ul className="list-unstyled mt-3">
                        {premiumFeatures.map((feature, index) => <DisabledFeatureItem key={index} {...feature} />)}
                    </ul>
                    <div className="text-center mt-4">
                        <button className="btn btn-primary" onClick={() => navigate('/subscriptionplans')}>
                            Upgrade Plan
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


export default function MySubscriptionPlan() {
    // ... (This part of the code remains the same, no changes needed)
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [view, setView] = useState('loading');

    useEffect(() => {
        if (authLoading) {
            setView('loading');
            return;
        }
        if (user) {
            const isTrial = user.planType?.toUpperCase() === 'TRIAL';
            if (isTrial) {
                setView('trial');
                setLoading(false);
            } else {
                const getSubscriptionData = async () => {
                    setLoading(true);
                    setError('');
                    try {
                        const resourceUrl = `${config.GETSUBSCRIPTIONBYCOMPANY}?companyId=${user.cid}`;
                        const response = await apiService.getAll(resourceUrl);
                        if (response && response.subscriptionId) {
                            setSubscription(response);
                            setView('subscription');
                        } else {
                            setError('No active subscription found.');
                            setView('error');
                        }
                    } catch (err) {
                        console.error("Failed to fetch subscription data:", err);
                        setError('Failed to load subscription details. Please try again.');
                        setView('error');
                    } finally {
                        setLoading(false);
                    }
                };
                getSubscriptionData();
            }
        } else {
            setError("You must be logged in to view your plan.");
            setView('error');
            setLoading(false);
        }
    }, [user, authLoading]);

    const { activeFeatures, disabledFeatures } = useMemo(() => {
        if (!subscription) return { activeFeatures: [], disabledFeatures: [] };
        const allFeatures = [
            { id: 'connects', icon: 'ri-copper-coin-line', text: `${subscription.connectsBalance} Connects Balance`, isEnabled: true },
            { id: 'maxUsersAllowed', icon: 'ri-user-line', text: `${subscription.features.maxUsersAllowed} Users Allowed`, isEnabled: subscription.features.maxUsersAllowed > 0 },
            { id: 'searchFlightsHotelsFromSuppliers', icon: 'ri-search-line', text: 'Search Flights & Hotels', isEnabled: subscription.features.searchFlightsHotelsFromSuppliers },
            { id: 'generateAIItinerary', icon: 'ri-magic-line', text: 'AI-Powered Itinerary Generation', isEnabled: subscription.features.generateAIItinerary },
            { id: 'whatsAppAIChatbot', icon: 'ri-whatsapp-line', text: 'AI WhatsApp Chatbot', isEnabled: subscription.features.whatsAppAIChatbot },
            { id: 'leadsFromSocialToDashboard', icon: 'ri-dashboard-line', text: 'Social Media Lead Capture', isEnabled: subscription.features.leadsFromSocialToDashboard },
            { id: 'reportsSuite', icon: 'ri-file-chart-line', text: 'Full Reports Suite', isEnabled: subscription.features.reportsSuite },
            { id: 'websiteCapability', icon: 'ri-window-line', text: `Website: ${websiteCapabilityMap[subscription.features.websiteCapability] || 'None'}`, isEnabled: subscription.features.websiteCapability !== 0 },
            { id: 'ownWebsiteServerAndDomain', icon: 'ri-server-line', text: 'Own Website Server & Domain', isEnabled: subscription.features.ownWebsiteServerAndDomain },
            { id: 'canUseOwnSupplierApiKeys', icon: 'ri-key-2-line', text: 'Use Own Supplier API Keys', isEnabled: subscription.features.canUseOwnSupplierApiKeys },
        ];
        return { activeFeatures: allFeatures.filter(f => f.isEnabled), disabledFeatures: allFeatures.filter(f => !f.isEnabled) };
    }, [subscription]);
    const formatDate = (dateString) => { if (!dateString) return 'N/A'; return format(new Date(dateString), 'dd MMM, yyyy'); };

    return (
        <>
            <Header />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="card">
                            <div className="p-2">
                                <MasterTabs pageName="MYPLAN" />
                            </div>
                            <div className="card-body p-4">
                                {view === 'loading' && <p className="text-center py-5">Loading your plan details...</p>}
                                {view === 'error' && <div className="alert alert-warning text-center">{error}</div>}
                                {view === 'trial' && <TrialUpsellView />}
                                {view === 'subscription' && subscription && (
                                    <div className="row">
                                        <div className="col-lg-4">
                                            <h4 className="fw-bold mb-4">Current Plan</h4>
                                            <div className="p-4 rounded mb-4" style={{ border: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}>
                                                <div className="d-flex justify-content-between align-items-center mb-3"><h5 className="mb-0 fw-semibold">{planEnumMap[subscription.plan] || 'Unknown Plan'}</h5><span className={`badge fs-6 bg-${subscription.status === 1 ? 'success' : 'warning'}`}>{statusEnumMap[subscription.status] || 'Unknown'}</span></div>
                                                <h2 className="fw-bolder">{subscription.currency} {subscription.pricePerCycle.toFixed(2)}<small className="fs-6 text-muted">/{billingCycleEnumMap[subscription.billingCycle] || 'Cycle'}</small></h2>
                                                <hr/>
                                                <div style={{ lineHeight: 1.8 }}><p className="mb-1"><span className="fw-semibold">Started On:</span> {formatDate(subscription.startDateUtc)}</p><p className="mb-1"><span className="fw-semibold">Current Period:</span> {formatDate(subscription.currentPeriodStartUtc)} - {formatDate(subscription.currentPeriodEndUtc)}</p><p className="mb-0"><span className="fw-semibold">Renews On:</span> {subscription.autoRenew ? formatDate(subscription.currentPeriodEndUtc) : 'Cancelled'}</p></div>
                                            </div>
                                            <div className="p-4 rounded d-flex justify-content-between align-items-center" style={{ border: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}>
                                                <div><h5 className="fw-bold mb-0">Want more connects?</h5><p className="text-muted mb-0">Top up your balance anytime.</p></div>
                                                <button className="btn btn-primary" onClick={() => navigate('/credithistory')}>Recharge</button>
                                            </div>
                                        </div>
                                        <div className="col-lg-4">
                                             <h4 className="fw-bold mb-4">Active Features</h4>
                                             <div className="p-4"><ul className="list-unstyled">{activeFeatures.map(feature => <FeatureItem key={feature.id} {...feature} />)}</ul></div>
                                        </div>
                                        {/* {disabledFeatures.length > 0 && (
                                            <div className="col-lg-4">
                                                <h4 className="fw-bold mb-4">Upgrade to Unlock</h4>
                                                <div className="p-4 rounded h-100" style={{ border: '1px solid #e9ecef', backgroundColor: '#FFF9F9' }}>
                                                    <ul className="list-unstyled">{disabledFeatures.map(feature => <DisabledFeatureItem key={feature.id} {...feature} />)}</ul>
                                                    <div className="text-center mt-4"><p className="fw-semibold mb-2">Upgrade your plan to activate these powerful features!</p><button className="btn btn-success" onClick={() => navigate('/subscriptionplans')}>Upgrade Plan</button></div>
                                                </div>
                                            </div>
                                        )} */}
                                        <div className="col-lg-4">
  <h4 className="fw-bold mb-4">Add-Ons</h4>

  {/* ADD-ON 1 */}
  <div
    className="p-4 rounded mb-4"
    style={{
      border: '1px solid #e9ecef',
      backgroundColor: '#F8FAFF'
    }}
  >
    <div className="d-flex justify-content-between align-items-center mb-2">
      <h5 className="fw-semibold mb-0">
        <i className="ri-window-line me-2 text-primary"></i>
        Launch Travel Website
      </h5>
      <span className="badge bg-primary fs-6">INR 999</span>
    </div>

    <p className="text-muted mb-3">
      Get your own branded travel website with hosting & payments.
    </p>

    <button
      className="btn btn-primary w-100"
      onClick={() => navigate('/subscriptionplans')}
    >
      Add Website
    </button>
  </div>

  {/* ADD-ON 2 */}
  <div
    className="p-4 rounded"
    style={{
      border: '1px solid #e9ecef',
      backgroundColor: '#F8FAFF'
    }}
  >
    <div className="d-flex justify-content-between align-items-center mb-2">
      <h5 className="fw-semibold mb-0">
        <i className="ri-server-line me-2 text-primary"></i>
        Launch Travel Portal
      </h5>
      <span className="badge bg-success fs-6">INR 1999</span>
    </div>

    <p className="text-muted mb-3">
      Full-fledged portal with flights, hotels & packages.
    </p>

    <button
      className="btn btn-success w-100"
      onClick={() => navigate('/subscriptionplans')}
    >
      Add Portal
    </button>
  </div>
</div>

                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}